import { NavLink, useNavigate } from "react-router-dom";
import { House, Compass, ProjectorScreen, Books, UsersThree, UserCircle, ShieldCheck } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import client from "@/lib/api";
import { NavLogo } from "@/components/NavLogo";
import { QuickIcons } from "@/components/QuickIcons";
import { CommandPalette } from "@/components/CommandPalette";
import { BroadcastBanner } from "@/components/BroadcastBanner";
import { InviteRewardNudge } from "@/components/InviteRewardNudge";
import { InviteCelebrationWatcher } from "@/components/InviteCelebration";
import { AppFooter } from "@/components/AppFooter";
import { TogetherSeatsIcon } from "@/components/nav/TogetherSeatsIcon";
import { ProfileMenu } from "@/components/nav/ProfileMenu";
import { useEffect, useRef, useState } from "react";

// The app's one and only navigation. Six primary destinations on desktop
// (You lives in the profile menu); all seven on the small-screen tab bar.
// Visual rules (Nov 2026 header redesign):
//  - thin cream line icons; ONLY the current page turns gold
//  - active = gold icon + label + glowing gold pill underneath
//  - no gold fills, arcs or glows on inactive items
const PRIMARY = [
  { to: "/app", label: "Home", icon: House, end: true, testid: "ia-nav-home" },
  { to: "/app/screening-room", label: "Discover", icon: Compass, testid: "ia-nav-discover" },
  { to: "/app/catalog", label: "Screening Room", icon: ProjectorScreen, testid: "ia-nav-screening-room" },
  { to: "/app/library", label: "Library", icon: Books, testid: "ia-nav-library" },
  { to: "/app/friends-hub", label: "Friends", icon: UsersThree, testid: "ia-nav-friends" },
  { to: "/app/together", label: "Together", icon: TogetherSeatsIcon, testid: "ia-nav-together" },
  { to: "/app/you", label: "You", icon: UserCircle, testid: "ia-nav-you" },
];

const CREAM = "text-[#E9E3D3]/80";
const FOCUS = "outline-none focus-visible:ring-2 focus-visible:ring-gold/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#07090d]";

/* ---------- small-screen bottom tab bar (unchanged behavior, new icons) ---------- */

function FriendsNavItem({ n, mobile }) {
  const { friendsBadge } = useNotifications();
  const navigate = useNavigate();
  const openRequests = (e) => { e.preventDefault(); e.stopPropagation(); navigate("/app/friends-hub?tab=requests"); };
  if (!mobile) return null;
  return (
    <div className="relative flex flex-col items-center">
      <NavLink to={n.to} end={n.end} data-testid={n.testid}
        className={({ isActive }) => `flex flex-col items-center gap-0.5 px-1 py-1 text-[10px] font-bold ${FOCUS} ${isActive ? "text-gold-soft" : "text-faint"}`}>
        {({ isActive }) => <><n.icon size={22} weight={isActive ? "bold" : "regular"} aria-hidden="true" />{n.label}</>}
      </NavLink>
      {friendsBadge > 0 && (
        <button onClick={openRequests} data-testid={`${n.testid}-badge`} aria-label={`${friendsBadge} new friend requests`}
          className="absolute -right-1 -top-0.5 flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-gold px-1 text-[9px] font-extrabold text-gold-ink">
          {friendsBadge > 9 ? "9+" : friendsBadge}
        </button>
      )}
    </div>
  );
}

/* ---------- desktop header items ---------- */

const TOP_ITEM_CLS = (isActive, scrolled) =>
  `group relative flex flex-col items-center justify-center gap-1 whitespace-nowrap rounded-lg px-2 pt-1 ` +
  `transition-[color,transform,padding] duration-200 ease-out motion-reduce:transition-none ` +
  `${scrolled ? "pb-1" : "pb-2.5"} text-[12px] xl:text-[13px] font-medium tracking-[0.01em] ${FOCUS} ${
    isActive ? "text-gold-soft" : `${CREAM} hover:-translate-y-px hover:text-[#F6F1E4] motion-reduce:hover:translate-y-0`
  }`;

function ItemFace({ n, isActive, pulseKey }) {
  const Icon = n.icon;
  return (
    <>
      <Icon key={pulseKey != null ? `pulse-${pulseKey}` : undefined} size={22} weight={isActive ? "regular" : "light"} aria-hidden="true"
        className={`h-5 w-5 xl:h-[22px] xl:w-[22px] transition-[filter] [transition-duration:200ms] ${pulseKey != null ? "animate-icon-pulse-x3 motion-reduce:animate-none " : ""}${
          isActive ? "drop-shadow-[0_0_6px_rgba(232,200,106,0.45)]" : "group-hover:drop-shadow-[0_0_5px_rgba(246,241,228,0.25)]"
        }`} />
      <span>{n.label}</span>
      {/* active pill; faint preview on hover */}
      <span aria-hidden="true"
        className={`pointer-events-none absolute -inset-x-1.5 bottom-0 h-[2px] rounded-full transition-opacity duration-200 motion-reduce:transition-none ${
          isActive
            ? "bg-gradient-to-r from-[#B8902C] via-[#F3D98A] to-[#B8902C] opacity-100 shadow-[0_0_8px_1px_rgba(232,200,106,0.4)]"
            : "bg-gold/40 opacity-0 group-hover:opacity-60"
        }`} />
    </>
  );
}

function TopItem({ n, pulseKey, scrolled, children }) {
  return (
    <div className="relative">
      <NavLink to={n.to} end={n.end} data-testid={n.testid} className={({ isActive }) => TOP_ITEM_CLS(isActive, scrolled)}>
        {({ isActive }) => <ItemFace n={n} isActive={isActive} pulseKey={pulseKey} />}
      </NavLink>
      {children}
    </div>
  );
}

function FriendsTopItem({ n, scrolled }) {
  const { friendsBadge } = useNotifications();
  const navigate = useNavigate();
  const openRequests = (e) => { e.preventDefault(); e.stopPropagation(); navigate("/app/friends-hub?tab=requests"); };
  return (
    <TopItem n={n} scrolled={scrolled}>
      {friendsBadge > 0 && (
        <button onClick={openRequests} data-testid={`${n.testid}-badge`} aria-label={`${friendsBadge} new friend requests`}
          className={`absolute left-1/2 top-[-4px] ml-1.5 flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-gradient-to-b from-[#F3D98A] to-[#D4AF37] px-1 text-[10px] font-extrabold text-gold-ink ring-2 ring-[#07090d] transition-opacity hover:opacity-90 ${FOCUS}`}>
          {friendsBadge > 9 ? "9+" : friendsBadge}
        </button>
      )}
    </TopItem>
  );
}

function HomeTopItem({ n, incomplete, scrolled }) {
  return (
    <TopItem n={n} scrolled={scrolled}>
      {incomplete && (
        <span data-testid="ia-nav-home-setup-dot" role="status" aria-label="Getting started setup incomplete"
          className="pointer-events-none absolute left-1/2 top-0.5 ml-2.5 h-[7px] w-[7px] rounded-full bg-gold shadow-[0_0_6px_2px_rgba(212,175,55,0.55)]" />
      )}
    </TopItem>
  );
}

function TogetherTopItem({ n, scrolled, pulseKey, count }) {
  const navigate = useNavigate();
  return (
    <TopItem n={n} scrolled={scrolled} pulseKey={pulseKey}>
      {count > 1 && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate("/app/watch-party?upcoming=1"); }}
          data-testid="ia-nav-together-badge"
          aria-label={`${count} upcoming watch parties — view your upcoming parties`}
          className={`absolute left-1/2 top-[-4px] ml-1.5 flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-gradient-to-b from-[#F3D98A] to-[#D4AF37] px-1 text-[10px] font-extrabold text-gold-ink ring-2 ring-[#07090d] transition-transform hover:scale-110 ${FOCUS}`}
        >
          {count > 9 ? "9+" : count}
        </button>
      )}
    </TopItem>
  );
}

function AdminButton() {
  return (
    <NavLink to="/app/admin/console" data-testid="ia-nav-admin" title="Admin" aria-label="Admin console"
      className={({ isActive }) =>
        `flex h-8 w-8 xl:h-9 xl:w-9 items-center justify-center rounded-full border transition-colors duration-200 ${FOCUS} ${
          isActive ? "border-gold/70 text-gold-soft" : "border-dashed border-[#E9E3D3]/[0.16] text-[#E9E3D3]/60 hover:border-gold/45 hover:text-[#F6F1E4]"
        }`}>
      <ShieldCheck size={17} weight="light" aria-hidden="true" />
    </NavLink>
  );
}

const Divider = () => (
  <span aria-hidden="true" className="h-10 w-px shrink-0 bg-gradient-to-b from-transparent via-gold/40 to-transparent xl:h-12" />
);

/* Cinematic frame: CSS-only gradients so the bar stays crisp at any width.
   Blue at the edges ties into the blue in the logo; gold stays near the
   bottom rule. Deliberately low opacity — the logo stays the hero. */
function HeaderBackdrop() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-[linear-gradient(180deg,#090B10_0%,#060709_70%,#050506_100%)] opacity-95" />
      {/* edge glows */}
      <div className="absolute -left-32 top-1/2 h-[240%] w-[400px] -translate-y-1/2 bg-[radial-gradient(closest-side,rgba(58,122,255,0.16),transparent)]" />
      <div className="absolute -right-32 top-1/2 h-[240%] w-[400px] -translate-y-1/2 bg-[radial-gradient(closest-side,rgba(58,122,255,0.12),transparent)]" />
      {/* light sweeps at the far corners */}
      <div className="absolute -left-56 -bottom-40 h-[230px] w-[560px] rounded-[50%] border-b border-[#5B93FF]/25" />
      <div className="absolute -right-56 -top-40 h-[230px] w-[560px] rounded-[50%] border-t border-gold/30" />
      {/* bottom rule + soft centre bloom — fades and draws itself in on first
          load only (NewRail mounts once per SPA session, not per route) */}
      <div className="absolute inset-x-0 bottom-0 h-px origin-center animate-header-rule-reveal bg-gradient-to-r from-transparent via-gold/85 to-transparent motion-reduce:animate-none" />
      <div className="absolute bottom-[-3px] left-1/2 h-[6px] w-[38%] origin-center animate-header-bloom-reveal rounded-full bg-gold/45 blur-md motion-reduce:animate-none motion-reduce:-translate-x-1/2" />
    </div>
  );
}

export function NewRail({ children }) {
  const { user } = useAuth();
  const { hasUpcomingWatchParty, upcomingWatchPartyCount } = useNotifications();
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [setupIncomplete, setSetupIncomplete] = useState(false);
  // Pulses the Together nav icon once (desktop + mobile) when the member
  // has an upcoming watch party — a brief, finite CSS animation (not a
  // persistent badge), so it draws the eye once per fresh occurrence
  // rather than running forever while a party stays scheduled.
  const [partyPulseKey, setPartyPulseKey] = useState(0);
  const hadPartyRef = useRef(false);

  useEffect(() => {
    client.get("/onboarding/status").then((r) => setSetupIncomplete(!r.data.all_resolved)).catch(() => {});
  }, []);

  useEffect(() => {
    const openPalette = () => setPaletteOpen(true);
    window.addEventListener("open-command-palette", openPalette);
    return () => window.removeEventListener("open-command-palette", openPalette);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (hasUpcomingWatchParty && !hadPartyRef.current) setPartyPulseKey((k) => k + 1);
    hadPartyRef.current = hasUpcomingWatchParty;
  }, [hasUpcomingWatchParty]);

  return (
    <div className="grain flex min-h-screen bg-obsidian">
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <InviteCelebrationWatcher />

      {/* ---------- desktop header (lg and up) ---------- */}
      <header data-testid="ia-nav-top-experiment"
        className={`fixed inset-x-0 top-0 z-30 hidden shadow-[0_10px_30px_rgba(0,0,0,0.45)] backdrop-blur-xl lg:block transition-[padding] duration-300 motion-reduce:transition-none ${scrolled ? "py-1" : "py-2"}`}>
        <HeaderBackdrop />
        <div className="relative mx-auto flex w-full max-w-sc-layout items-center gap-3 px-4 md:px-8 xl:gap-5">
          <button onClick={() => navigate("/app")} data-testid="ia-nav-brand" aria-label="Screening Circle — Home"
            className={`shrink-0 rounded-lg transition-transform duration-200 hover:scale-[1.03] motion-reduce:hover:scale-100 ${FOCUS}`}>
            <NavLogo className={`w-auto drop-shadow-[0_4px_14px_rgba(0,0,0,0.6)] transition-[height] duration-300 motion-reduce:transition-none ${scrolled ? "h-14 xl:h-16" : "h-20 xl:h-24"}`} />
          </button>

          {/* Menu row sits low, lined up with the bottom of the logo
              (not centred on it), so the logo reads as the hero above it. */}
          <div className={`flex min-w-0 flex-1 items-center gap-3 self-end xl:gap-5 transition-[padding] duration-300 motion-reduce:transition-none ${scrolled ? "pb-0" : "pb-2.5"}`}>
            <Divider />

            <nav aria-label="Main" className="flex min-w-0 flex-1 items-center justify-evenly gap-1 xl:gap-2">
              {PRIMARY.filter((n) => n.to !== "/app/you").map((n) =>
                n.to === "/app/friends-hub" ? <FriendsTopItem key={n.to} n={n} scrolled={scrolled} />
                : n.to === "/app" ? <HomeTopItem key={n.to} n={n} incomplete={setupIncomplete} scrolled={scrolled} />
                : n.to === "/app/together" ? <TogetherTopItem key={n.to} n={n} scrolled={scrolled} pulseKey={hasUpcomingWatchParty ? partyPulseKey : null} count={upcomingWatchPartyCount} />
                : <TopItem key={n.to} n={n} scrolled={scrolled} />
              )}
            </nav>

            <Divider />

            <div className="flex shrink-0 items-center gap-2 xl:gap-2.5">
              {user?.is_admin && <AdminButton />}
              <QuickIcons variant="header" />
            </div>

            <ProfileMenu />
          </div>
        </div>
      </header>

      {/* ---------- small-screen top bar (below lg) ---------- */}
      <div className="fixed inset-x-0 top-0 z-30 flex items-center justify-between border-b border-white/[0.08] bg-obsidian/80 px-4 py-3 backdrop-blur-xl md:px-8 lg:hidden">
        <NavLogo height={84} />
        <div className="flex shrink-0 items-center gap-2">
          <QuickIcons variant="bar" showAvatar />
        </div>
        <span aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-px origin-center animate-header-rule-reveal bg-gradient-to-r from-transparent via-gold/70 to-transparent motion-reduce:animate-none" />
      </div>

      {/* ---------- small-screen bottom tab bar (below lg) ---------- */}
      <nav aria-label="Main" className="fixed inset-x-0 bottom-0 z-30 flex items-center justify-around border-t border-white/[0.08] bg-obsidian/95 py-2 backdrop-blur-xl lg:hidden">
        {PRIMARY.map((n) => n.to === "/app/friends-hub" ? (
          <FriendsNavItem key={n.to} n={{ ...n, testid: `mobile-${n.testid}` }} mobile />
        ) : n.to === "/app/together" ? (
          <div key={n.to} className="relative">
            <NavLink to={n.to} end={n.end} data-testid={`mobile-${n.testid}`}
              className={({ isActive }) => `relative flex flex-col items-center gap-0.5 px-1 py-1 text-[10px] font-bold ${FOCUS} ${isActive ? "text-gold-soft" : "text-faint"}`}>
              {({ isActive }) => (
                <>
                  <n.icon
                    key={hasUpcomingWatchParty ? `pulse-${partyPulseKey}` : undefined}
                    size={22} weight={isActive ? "bold" : "regular"} aria-hidden="true"
                    className={hasUpcomingWatchParty ? "animate-icon-pulse-x3 motion-reduce:animate-none" : undefined}
                  />
                  {n.label}
                </>
              )}
            </NavLink>
            {upcomingWatchPartyCount > 1 && (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate("/app/watch-party?upcoming=1"); }}
                data-testid="mobile-ia-nav-together-badge"
                aria-label={`${upcomingWatchPartyCount} upcoming watch parties — view your upcoming parties`}
                className={`absolute right-0.5 top-0 flex h-[13px] min-w-[13px] items-center justify-center rounded-full bg-gradient-to-b from-[#F3D98A] to-[#D4AF37] px-0.5 text-[8px] font-extrabold text-gold-ink ring-1 ring-[#07090d] ${FOCUS}`}
              >
                {upcomingWatchPartyCount > 9 ? "9+" : upcomingWatchPartyCount}
              </button>
            )}
          </div>
        ) : (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={`mobile-${n.testid}`}
            className={({ isActive }) => `relative flex flex-col items-center gap-0.5 px-1 py-1 text-[10px] font-bold ${FOCUS} ${isActive ? "text-gold-soft" : "text-faint"}`}>
            {({ isActive }) => (
              <>
                <n.icon size={22} weight={isActive ? "bold" : "regular"} aria-hidden="true" />{n.label}
                {n.to === "/app" && setupIncomplete && (
                  <span data-testid="mobile-ia-nav-home-setup-dot" aria-label="Getting started setup incomplete"
                    className="absolute right-0.5 top-0 h-[7px] w-[7px] rounded-full bg-gold shadow-[0_0_6px_2px_rgba(212,175,55,0.55)]" />
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <main className="relative z-10 min-h-screen min-w-0 flex-1 pb-24 pt-32">
        <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 opacity-60" style={{ background: "radial-gradient(ellipse 60% 50% at 50% 0%, rgba(212,175,55,0.05), transparent 70%)" }} />
        {/* Intentionally no BackBar here — the persistent nav handles navigation (per spec) */}
        <div className="mx-auto max-w-sc-layout px-4 md:px-8">
          <BroadcastBanner />
          <InviteRewardNudge />
          {children}
          <AppFooter />
        </div>
      </main>
    </div>
  );
}
